/* @flow */
import * as React from 'react';
import { reaction, observable, computed } from 'mobx';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';
import { Link, withRouter } from 'react-router-dom';

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import Button from '@material-ui/core/Button';

import SentRequestTableItem from 'components/SentRequestTableItem/SentRequestTableItem';
import Loading from 'components/Loading/Loading';
import DashboardStore from 'stores/DashboardStore';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import NotificationStore, { Subscription } from 'stores/NotificationStore';

import styles from './SentRequests.module.scss';

type InjectedProps = {
  notificationStore: NotificationStore,
  dashboardStore: DashboardStore,
} & TranslatorProps;

@translate()
@inject('dashboardStore', 'notificationStore')
@withRouter
@observer
export default class SentRequests extends ComponentWithInjectedProps<{}, {}, InjectedProps> {
  autoUpdateDisposer: Function;

  @observable sentRequestsSubscription: ?Subscription;

  @computed get sentRequests() {
    return this.sentRequestsSubscription ? this.sentRequestsSubscription.data : [];
  }

  componentDidMount() {
    const { dashboardStore, notificationStore } = this.props;

    if (!this.sentRequestsSubscription) {
      this.autoUpdateDisposer = reaction(
        () => dashboardStore.loadedTradingAccount,
        tradingAccount => {
          if (this.sentRequestsSubscription) {
            notificationStore.unsubscribeFromNotifications(this.sentRequestsSubscription.id);
            this.sentRequestsSubscription = undefined;
          }

          this.sentRequestsSubscription = notificationStore.subscribeToNotifications(
            {
              type: 'sent-requests',
              urlParams: [
                ...(tradingAccount !== 0 ? [{ key: 'id', values: [tradingAccount] }] : []),
              ],
              searchParams: [],
            },
            { background: false }
          );
        },
        { fireImmediately: true }
      );
    }
  }

  componentWillUnmount() {
    const { notificationStore, match } = this.props;

    if (!match.path.includes('/welcome-page')) {
      if (this.sentRequestsSubscription) {
        notificationStore.unsubscribeFromNotifications(this.sentRequestsSubscription.id);
        this.sentRequestsSubscription = undefined;
      }

      this.autoUpdateDisposer();
    }
  }

  render() {
    const { t } = this.props;

    return (
      <Card className={styles['d-widget']}>
        <CardHeader
          className={styles['d-widget-header']}
          title={<Typography variant="headline">{t('general:messages.request_sent')}</Typography>}
          action={
            <Link to={{ pathname: '/notifications', state: { tab: 0 } }}>
              <Button variant="contained" color="primary" style={{ marginRight: '12px' }}>
                {t('general:actions.view_sent_requests')}
              </Button>
            </Link>
          }
        />
        <Divider />
        <CardContent className={styles['d-widget-content']}>
          {!this.sentRequestsSubscription || this.sentRequestsSubscription.loading ? (
            <Loading />
          ) : this.sentRequests && this.sentRequests.length ? (
            <List disablePadding={true}>
              {this.sentRequests.map(notification => {
                return <SentRequestTableItem key={notification.id} notification={notification} />;
              })}
            </List>
          ) : (
            <div className={styles['no-data']}>{t('general:dashboards.messages.no_requests')}</div>
          )}
        </CardContent>
      </Card>
    );
  }
}
